/**
 * RealEstateAssetsForm — Screen 3: Real Estate & Other Assets
 *
 * Pure form component (no layout). Renders property, business interests,
 * insurance, and depreciating assets. Separated from the Investable Assets
 * form to distinguish physical/illiquid assets from liquid portfolio.
 *
 * Used by mobile SettingsPageLayout and desktop SettingsModal.
 */

import CurrencyInput from "../components/CurrencyInput";
import { SectionHeader } from "../components/SectionHeader";
import { MarketStatistic } from "../components/MarketStatistic";
import { useRealEstateAssetsLogic } from "./RealEstateAssets.logic";
import { useFieldStatistic } from "../hooks/useStatistics";
import { useStatisticsValue } from "../hooks/useStatisticsValue";

/**
 * RealEstateAssetsForm - Pure form component (no layout)
 *
 * Displays all real estate & other asset fields organized by section:
 * - Real Estate (essential + advanced)
 * - Business Interests (advanced)
 * - Insurance Assets (advanced)
 * - Other Assets (essential + advanced)
 */
export function RealEstateAssetsForm() {
  const { formData, handleChange, handleBlur, loading, totalRealEstateAssets } =
    useRealEstateAssetsLogic();

  // Statistic values for real estate assets category
  const assetsStatistic = useStatisticsValue("realEstateAssets");

  const primaryResidenceHelp = useFieldStatistic(
    "primaryResidence",
    "Current market value of your home"
  );
  const investmentPropertiesHelp = useFieldStatistic(
    "investmentProperties",
    "Total value of rental or investment properties"
  );
  const vacationPropertyHelp = useFieldStatistic(
    "vacationProperty",
    "Cottage, cabin, or vacation home value"
  );
  const landHelp = useFieldStatistic(
    "landOtherRealEstate",
    "Vacant land, farmland, or commercial real estate"
  );
  const businessHelp = useFieldStatistic(
    "businessValue",
    "Private corporation shares, partnership interests, practice value"
  );
  const shareholderLoansHelp = useFieldStatistic(
    "shareholderLoansReceivable",
    "Amounts owed to you by your corporation"
  );
  const insuranceHelp = useFieldStatistic(
    "insuranceCashValue",
    "Cash surrender value of whole life or universal life policies"
  );
  const otherAssetsHelp = useFieldStatistic(
    "otherAssets",
    "Collectibles, art, gold, precious metals, private equity"
  );
  const depreciatingHelp = useFieldStatistic(
    "depreciatingAssets",
    "Vehicles, boats, electronics, furniture, household contents"
  );

  if (loading) {
    return null;
  }

  return (
    <div className="settings-form-container" id="real-estate-assets-form">
      <form className="settings-form-scroll">
        {/* Section: Real Estate */}
        <SectionHeader title="Real Estate" sectionId="real-estate" />
        <div className="form-grid">
          <CurrencyInput
            name="primaryResidence"
            value={formData.primaryResidence}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Primary Residence"
            helpText={primaryResidenceHelp}
          />
          <CurrencyInput
            name="vacationProperty"
            value={formData.vacationProperty}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Vacation Property"
            helpText={vacationPropertyHelp}
          />
          <CurrencyInput
            name="investmentProperties"
            value={formData.investmentProperties}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Investment Properties"
            helpText={investmentPropertiesHelp}
          />
          <CurrencyInput
            name="landOtherRealEstate"
            value={formData.landOtherRealEstate}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Land & Other Real Estate"
            helpText={landHelp}
          />
        </div>

        {/* Section: Business Interests */}
        <SectionHeader title="Business Interests" sectionId="business" />
        <div className="form-grid">
          <CurrencyInput
            name="businessValue"
            value={formData.businessValue}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Business Value"
            helpText={businessHelp}
          />
          <CurrencyInput
            name="shareholderLoansReceivable"
            value={formData.shareholderLoansReceivable}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Shareholder Loans Receivable"
            helpText={shareholderLoansHelp}
          />
          <CurrencyInput
            name="insuranceCashValue"
            value={formData.insuranceCashValue}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Insurance Cash Value"
            helpText={insuranceHelp}
          />
        </div>

        {/* Section: Other Assets */}
        <SectionHeader title="Other Assets" sectionId="other-assets" />
        <div className="form-grid">
          <CurrencyInput
            name="otherAssets"
            value={formData.otherAssets}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Other Appreciating Assets"
            helpText={otherAssetsHelp}
          />
          <CurrencyInput
            name="depreciatingAssets"
            value={formData.depreciatingAssets}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Depreciating Assets"
            helpText={depreciatingHelp}
          />
        </div>
      </form>

      {/* Total (calculated, read-only) - Sticky at bottom for long forms */}
      <div className="form-sticky-save" id="real-estate-total-bar">
        <div className="form-total-statistic-spacing">
          <MarketStatistic
            label="Canadian Real Estate Statistic"
            marketAverage={assetsStatistic}
            currency={true}
          />
        </div>
        <div className="form-page-total-row">
          <span className="form-page-total-label">
            Total Real Estate & Other Assets
          </span>
          <span id="total" className="form-page-total-value-blue">
            ${totalRealEstateAssets.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}
